if (!customElements.get("anniversary-featured-story")) {
  class AnniversaryFeaturedStory extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll("[data-story-slide]"));
      this.templates = Array.from(this.querySelectorAll("[data-story-template]"));
      this.dialog = this.querySelector("[data-story-dialog]");
      this.dialogBody = this.querySelector("[data-story-dialog-body]");
      this.closeButton = this.querySelector("[data-story-close]");
      this.prevButton = this.querySelector("[data-story-prev]");
      this.nextButton = this.querySelector("[data-story-next]");
      this.abortController = new AbortController();
      this.transitionTimer = 0;
      this.isTransitioning = false;
      this.hasInitialized = false;
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.transitionDuration = 520;
      this.index = this.slides.findIndex((slide) => slide.classList.contains("is-active"));

      if (this.index < 0) this.index = 0;

      const signal = this.abortController.signal;

      this.querySelectorAll("[data-story-open]").forEach((button) => {
        button.addEventListener("click", () => {
          this.openDialog(Number(button.dataset.storyOpen) || 0);
        }, { signal });
      });

      this.prevButton?.addEventListener("click", () => this.showSlide(this.index - 1), { signal });
      this.nextButton?.addEventListener("click", () => this.showSlide(this.index + 1), { signal });
      this.closeButton?.addEventListener("click", () => this.closeDialog(), { signal });

      this.dialog?.addEventListener("click", (event) => {
        if (event.target === this.dialog) this.closeDialog();
      }, { signal });

      this.dialog?.addEventListener("close", () => this.clearDialog(), { signal });

      this.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft") this.showSlide(this.index - 1);
        if (event.key === "ArrowRight") this.showSlide(this.index + 1);
        if (event.key === "Escape" && this.dialog?.open) this.closeDialog();
      }, { signal });

      this.showSlide(this.index, { instant: true });
      this.hasInitialized = true;
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.clearTimeout(this.transitionTimer);
      this.clearDialog();
    }

    showSlide(nextIndex, options = {}) {
      if (!this.slides.length) return;
      if (this.isTransitioning && !options.instant) return;

      const normalizedIndex = Math.max(0, Math.min(nextIndex, this.slides.length - 1));
      const previousIndex = this.index;
      const shouldAnimate =
        this.hasInitialized &&
        !options.instant &&
        !this.reducedMotion &&
        previousIndex !== normalizedIndex;

      if (!shouldAnimate && previousIndex === normalizedIndex && this.hasInitialized) {
        this.syncSlideState(normalizedIndex);
        return;
      }

      const direction = normalizedIndex >= previousIndex ? 1 : -1;
      const outgoing = this.slides[previousIndex];
      const incoming = this.slides[normalizedIndex];

      this.index = normalizedIndex;
      this.syncControlState(normalizedIndex);

      if (!shouldAnimate) {
        this.slides.forEach((slide, slideIndex) => {
          slide.classList.remove("is-leaving");
          slide.style.removeProperty("--story-slide-offset");
          slide.style.removeProperty("--story-slide-leave-offset");
          this.setSlideActive(slide, slideIndex === normalizedIndex);
        });
        return;
      }

      this.isTransitioning = true;
      window.clearTimeout(this.transitionTimer);

      this.slides.forEach((slide) => {
        slide.classList.remove("is-leaving");
        slide.style.removeProperty("--story-slide-offset");
        slide.style.removeProperty("--story-slide-leave-offset");
      });

      outgoing.classList.add("is-leaving");
      outgoing.style.setProperty("--story-slide-leave-offset", `${direction * -24}px`);
      incoming.style.setProperty("--story-slide-offset", `${direction * 24}px`);

      // Force the incoming slide to start off-screen before fading in.
      incoming.classList.remove("is-active");
      void incoming.offsetWidth;

      this.slides.forEach((slide, slideIndex) => {
        this.setSlideActive(slide, slideIndex === normalizedIndex);
      });

      this.transitionTimer = window.setTimeout(() => {
        outgoing.classList.remove("is-leaving");
        outgoing.style.removeProperty("--story-slide-leave-offset");
        incoming.style.removeProperty("--story-slide-offset");
        this.isTransitioning = false;
        this.transitionTimer = 0;
      }, this.transitionDuration);
    }

    setSlideActive(slide, isActive) {
      slide.classList.toggle("is-active", isActive);

      if (isActive) {
        slide.removeAttribute("aria-hidden");
      } else {
        slide.setAttribute("aria-hidden", "true");
      }

      slide.querySelectorAll("button, a, [tabindex]").forEach((control) => {
        if (isActive) {
          control.removeAttribute("tabindex");
        } else {
          control.setAttribute("tabindex", "-1");
        }
      });
    }

    syncSlideState(normalizedIndex) {
      this.slides.forEach((slide, slideIndex) => {
        slide.classList.remove("is-leaving");
        this.setSlideActive(slide, slideIndex === normalizedIndex);
      });
      this.syncControlState(normalizedIndex);
    }

    syncControlState(normalizedIndex) {
      const hasMultipleSlides = this.slides.length > 1;
      this.updateControlState(this.prevButton, !hasMultipleSlides || normalizedIndex === 0);
      this.updateControlState(this.nextButton, !hasMultipleSlides || normalizedIndex === this.slides.length - 1);
    }

    updateControlState(button, disabled) {
      if (!button) return;

      button.disabled = disabled;
      button.setAttribute("aria-disabled", String(disabled));
    }

    openDialog(index) {
      if (!this.dialog || !this.dialogBody) return;

      const template = this.templates.find((item) => Number(item.dataset.storyTemplate) === index);
      if (!template) return;

      this.clearDialog();
      this.dialogBody.appendChild(template.content.cloneNode(true));
      this.mountVideo();

      if (typeof this.dialog.showModal === "function") {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute("open", "");
      }

      this.closeButton?.focus({ preventScroll: true });
    }

    closeDialog() {
      if (!this.dialog) return;

      if (typeof this.dialog.close === "function") {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute("open");
        this.clearDialog();
      }
    }

    clearDialog() {
      if (!this.dialogBody) return;
      this.dialogBody.replaceChildren();
    }

    mountVideo() {
      const player = this.dialogBody?.querySelector("[data-story-detail-player]");
      if (!player) return;

      const videoId = this.pickVideoId(player);
      if (!videoId) return;

      const iframe = document.createElement("iframe");
      iframe.className = "anniversary-featured-story__detail-iframe";
      iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0&playsinline=1`;
      iframe.title = player.dataset.videoTitle || "Story video";
      iframe.allow = "autoplay; encrypted-media; picture-in-picture";
      iframe.allowFullscreen = true;

      player.replaceChildren(iframe);
    }

    pickVideoId(player) {
      const width = window.innerWidth;
      const videos = {
        pc: player.dataset.videoPc || "",
        tablet: player.dataset.videoTablet || "",
        mobile: player.dataset.videoMobile || "",
      };
      const orderedValues = width < 768
        ? [videos.mobile, videos.tablet, videos.pc]
        : width < 1280
          ? [videos.tablet, videos.pc, videos.mobile]
          : [videos.pc, videos.tablet, videos.mobile];
      const rawValue = orderedValues.find((value) => value.trim()) || "";

      return this.extractYoutubeId(rawValue);
    }

    extractYoutubeId(value) {
      const trimmedValue = value.trim();
      if (!trimmedValue) return "";
      if (/^[a-zA-Z0-9_-]{11}$/.test(trimmedValue)) return trimmedValue;

      try {
        const url = new URL(trimmedValue);
        if (url.hostname.includes("youtu.be")) return url.pathname.replace("/", "");
        if (url.searchParams.has("v")) return url.searchParams.get("v") || "";
        const embedMatch = url.pathname.match(/\/embed\/([^/?]+)/);
        return embedMatch ? embedMatch[1] : "";
      } catch (error) {
        return "";
      }
    }
  }

  customElements.define("anniversary-featured-story", AnniversaryFeaturedStory);
}
